/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useMemo, useRef, useState } from "react";
import "@/styles/components.css";
import {
  FilterType,
  FILTER_OPTIONS,
} from "@/constants/grid/YearFilterConstants";
import YearFilterModel from "@/interfaces/YearFilterModel";
import { sanitizeYearInput, isValidYear } from "@/utils/Validation";
import YearFilterProps from "@/interfaces/YearFilterProps";

const YearFilter = (props: YearFilterProps) => {
  const { model, onModelChange } = props;

  const [type, setType] = useState<FilterType>(
    model?.type || FilterType.EQUALS,
  );

  const [filter, setFilter] = useState<string>(
    model?.filter != null ? String(model.filter) : "",
  );

  const [filterTo, setFilterTo] = useState<string>(
    model?.filterTo != null ? String(model.filterTo) : "",
  );

  const isRange = type === FilterType.IN_RANGE;
  // Keep previous model to avoid duplicate onModelChange calls
  // initialize to null (no active model) to simplify comparisons
  const prevModelRef = useRef<YearFilterModel | null>(null);
  // track previous sanitized raw input values to detect transitions (e.g., 3->4 digits, or N->0)
  const initialPrevFilter = model?.filter != null ? String(model.filter) : "";
  const initialPrevFilterTo =
    model?.filterTo != null ? String(model.filterTo) : "";
  const prevFilterValueRef = useRef<string>(initialPrevFilter);
  const prevFilterToValueRef = useRef<string>(initialPrevFilterTo);
  // track previous input lengths for transition detection
  const prevInputLenRef = useRef<{ filterLen: number; filterToLen: number }>({
    filterLen: initialPrevFilter.length,
    filterToLen: initialPrevFilterTo.length,
  });
  // no inputType tracking - we rely on typed transitions only
  // track last typed values (previous render) to detect precise transitions (3->4 and 1->0)
  const prevTypedRef = useRef<{ filter: string; filterTo: string }>({
    filter: initialPrevFilter,
    filterTo: initialPrevFilterTo,
  });
  // mark that the displayed input(s) have changed since the last applied model
  const dirtySinceApplyRef = useRef<boolean>(false);
  // debounce timer ref (browser setTimeout returns number)
  const debounceTimerRef = useRef<number | null>(null);

  const isSingleValid = useMemo(() => {
    const sanitizedFilter = sanitizeYearInput(filter);
    // For accessibility/UX: treat empty input as valid (no active filter)
    if (sanitizedFilter.length === 0) return true;
    return sanitizedFilter.length === 4 && isValidYear(sanitizedFilter);
  }, [filter]);

  const isRangeValid = useMemo(() => {
    const sanitizedFrom = sanitizeYearInput(filter);
    const sanitizedTo = sanitizeYearInput(filterTo);
    // For accessibility/UX: empty range (both fields empty) is valid
    if (sanitizedFrom.length === 0 && sanitizedTo.length === 0) return true;

    // If one of the fields is missing or not a full 4-digit year, range is invalid
    if (sanitizedFrom.length !== 4 || sanitizedTo.length !== 4) return false;

    if (!isValidYear(sanitizedFrom) || !isValidYear(sanitizedTo)) return false;
    const from = Number(sanitizedFrom);
    const to = Number(sanitizedTo);
    return from <= to;
  }, [filter, filterTo]);

  // Sync to external `model` prop changes only. We no longer call `onModelChange` from here.
  useEffect(() => {
    const incomingJson = JSON.stringify(model ?? null);
    const prevJson = JSON.stringify(prevModelRef.current);
    if (incomingJson !== prevJson) {
      // external injection: sync prevModelRef and prev raw trackers
      prevModelRef.current = model ?? null;
      prevFilterValueRef.current =
        model?.filter != null ? String(model.filter) : "";
      prevFilterToValueRef.current =
        model?.filterTo != null ? String(model.filterTo) : "";

      // also sync internal state so inputs reflect the externally set model
      setType(model?.type ?? FilterType.EQUALS);
      setFilter(model?.filter != null ? String(model.filter) : "");
      setFilterTo(model?.filterTo != null ? String(model.filterTo) : "");

      // sync input length tracking to the externally applied model
      prevInputLenRef.current = {
        filterLen: model?.filter != null ? String(model.filter).length : 0,
        filterToLen:
          model?.filterTo != null ? String(model.filterTo).length : 0,
      };
      // sync typed refs too
      prevTypedRef.current = {
        filter: model?.filter != null ? String(model.filter) : "",
        filterTo: model?.filterTo != null ? String(model.filterTo) : "",
      };
    }

    // cleanup for any pending debounce timer on unmount
    return () => {
      const t = debounceTimerRef.current;
      if (t != null) {
        window.clearTimeout(t);
        debounceTimerRef.current = null;
      }
    };
  }, [model]);

  // Helper: apply model (with debounce) and update prev trackers when applied.
  const applyModel = (nextModel: YearFilterModel | null, force = false) => {
    let debounceMs = 0;
    if (typeof props.filterParams?.debounceMs === "number") {
      debounceMs = props.filterParams.debounceMs;
    } else if (typeof props.colDef?.filterParams?.debounceMs === "number") {
      debounceMs = props.colDef.filterParams.debounceMs;
    }

    // clear any pending timer
    const existing = debounceTimerRef.current;
    if (existing != null) {
      window.clearTimeout(existing);
      debounceTimerRef.current = null;
    }

    const doApply = () => {
      // Ensure the last typed values match the model we're about to apply.
      const lastTyped = prevTypedRef.current?.filter ?? "";
      const lastTypedTo = prevTypedRef.current?.filterTo ?? "";
      if (nextModel === null) {
        // only apply clear when the displayed typed value is empty
        const shouldBeEmpty =
          lastTyped.length === 0 && (!isRange || lastTypedTo.length === 0);
        if (!shouldBeEmpty) return;
      } else {
        // only apply when the displayed typed values equal the nextModel values
        const nextFilterStr =
          nextModel.filter != null ? String(nextModel.filter) : "";
        const nextFilterToStr =
          nextModel.filterTo != null ? String(nextModel.filterTo) : "";
        if (!isRange) {
          if (lastTyped !== nextFilterStr) return;
        } else {
          const matches =
            lastTyped === nextFilterStr && lastTypedTo === nextFilterToStr;
          if (!matches) return;
        }
      }
      // Guard: ensure this apply corresponds to allowed transitions
      const prevApplied = prevFilterValueRef.current || "";
      const prevAppliedTo = prevFilterToValueRef.current || "";

      // Single-field rules
      if (!isRange) {
        if (nextModel === null) {
          // only apply clear when there was previously a value (unless forced)
          if (prevApplied.length === 0 && !force) return;
        } else {
          const nextFilterStr =
            nextModel.filter != null ? String(nextModel.filter) : "";
          const prevWasFour = prevApplied.length === 4;
          const shouldApply =
            nextFilterStr.length === 4 && (force || !prevWasFour);
          if (!shouldApply) return;
        }
      } else {
        // Range rules: both must be present (4) to apply, and either previously not both 4 or forced
        if (nextModel === null) {
          if (
            prevApplied.length === 0 &&
            prevAppliedTo.length === 0 &&
            !force
          ) {
            return;
          }
        } else {
          const nextA =
            nextModel.filter != null ? String(nextModel.filter) : "";
          const nextB =
            nextModel.filterTo != null ? String(nextModel.filterTo) : "";
          const bothFour = nextA.length === 4 && nextB.length === 4;
          const prevBothFour =
            prevApplied.length === 4 && prevAppliedTo.length === 4;
          const shouldApply = bothFour && (force || !prevBothFour);
          if (!shouldApply) {
            return;
          }
        }
      }

      // Prevent applying if model didn't change and there was no user edit since last apply
      const prevJson = JSON.stringify(prevModelRef.current);
      const nextJson = JSON.stringify(nextModel);
      if (!force && prevJson === nextJson && !dirtySinceApplyRef.current) {
        // nothing to do — skip applying when the model is unchanged and not dirty
        return;
      }

      // debug log
      prevModelRef.current = nextModel;
      prevFilterValueRef.current =
        nextModel && nextModel.filter != null ? String(nextModel.filter) : "";
      prevFilterToValueRef.current =
        nextModel && nextModel.filterTo != null
          ? String(nextModel.filterTo)
          : "";
      // clear dirty flag: we've applied the model and display matches applied model
      dirtySinceApplyRef.current = false;
      onModelChange(nextModel);
    };

    // If force requested, bypass debounce and apply immediately
    if (force || debounceMs <= 0) {
      doApply();
      return;
    }

    debounceTimerRef.current = window.setTimeout(() => {
      doApply();
      debounceTimerRef.current = null;
    }, debounceMs) as unknown as number;
  };

  // NOTE: removed duplicate/dead debounce code that existed after the JSX return.

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextType = e.target.value as FilterType;
    setType(nextType);

    // Clear values when switching types to prevent invalid states
    setFilter("");
    setFilterTo("");
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // keep for backward compatibility but prefer onInput for apply triggers
    const next = sanitizeYearInput(e.target.value);
    setFilter(next);
    // mark dirty if displayed values differ from last applied model
    dirtySinceApplyRef.current =
      next !== (prevFilterValueRef.current || "") ||
      filterTo !== (prevFilterToValueRef.current || "");
  };

  const handleFilterToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextTo = sanitizeYearInput(e.target.value);
    setFilterTo(nextTo);
    // mark dirty if displayed values differ from last applied model
    dirtySinceApplyRef.current =
      filter !== (prevFilterValueRef.current || "") ||
      nextTo !== (prevFilterToValueRef.current || "");
  };
  // EFFECT: single-field transitions: trigger only on exact 3->4 completion or clearing to empty
  useEffect(() => {
    if (isRange) return;

    const curr = sanitizeYearInput(filter);

    // Use the last typed value as the previous value to detect user transitions
    const prevTyped = prevTypedRef.current?.filter ?? "";
    const prevTypedLen = prevTyped.length;

    // Detect transitions:
    // - entering the 4th digit (prevTypedLen < 4 && now === 4)
    // - or retyping completed 4 digits after previously having 2 digits (edge case)
    // - erasing the last digit to empty (prevTypedLen > 0 && now === 0)
    const prevRecordedLen = prevInputLenRef.current.filterLen ?? prevTypedLen;
    const enteredFourth =
      (prevTypedLen < 4 && curr.length === 4) ||
      (prevRecordedLen === 2 && curr.length === 4);
    const erasedToEmpty = prevTypedLen > 0 && curr.length === 0;

    if (!enteredFourth && !erasedToEmpty) {
      // no relevant transition -> update trackers and exit
      prevInputLenRef.current.filterLen = curr.length;
      prevTypedRef.current.filter = curr;
      return;
    }

    // Entered 4th digit -> apply if valid and model changed
    if (enteredFourth) {
      // update typed trackers first so applyModel sees the current displayed value
      prevTypedRef.current.filter = curr;
      prevInputLenRef.current.filterLen = curr.length;

      if (isValidYear(curr)) {
        // Attempt to apply. applyModel will decide based on dirty flag and previous model
        applyModel({ filterType: "number", type, filter: Number(curr) }, true);
      }
      return;
    }

    // Erased to empty -> apply clear if previously applied had a value
    if (erasedToEmpty) {
      // update trackers first
      prevTypedRef.current.filter = curr;
      prevInputLenRef.current.filterLen = curr.length;
      const prevApplied = prevFilterValueRef.current || "";
      if (prevApplied.length !== 0) {
        applyModel(null, true);
      }
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isRange, type]);

  // EFFECT: range transitions: trigger only when both fields completed from <4->4 on at least one side,
  // or when both cleared to empty
  useEffect(() => {
    if (!isRange) return;

    const currA = sanitizeYearInput(filter);
    const currB = sanitizeYearInput(filterTo);

    // Use last typed values as previous values to detect transitions for range
    const prevTypedA = prevTypedRef.current?.filter ?? "";
    const prevTypedB = prevTypedRef.current?.filterTo ?? "";
    const prevALen = prevTypedA.length;
    const prevBLen = prevTypedB.length;

    // Entered completion when BOTH are 4 now and at least one transitioned into 4
    const bothNowFour = currA.length === 4 && currB.length === 4;
    const prevRecordedALen = prevInputLenRef.current.filterLen ?? prevALen;
    const prevRecordedBLen = prevInputLenRef.current.filterToLen ?? prevBLen;
    const oneJustCompleted =
      (prevALen < 4 && currA.length === 4) ||
      (prevBLen < 4 && currB.length === 4) ||
      (prevRecordedALen === 2 && currA.length === 4) ||
      (prevRecordedBLen === 2 && currB.length === 4);
    const enteredCompleting = bothNowFour && oneJustCompleted;

    // Both erased to empty
    const erasedBothToEmpty =
      currA.length === 0 &&
      currB.length === 0 &&
      (prevALen > 0 || prevBLen > 0);

    if (!enteredCompleting && !erasedBothToEmpty) {
      prevInputLenRef.current.filterLen = currA.length;
      prevInputLenRef.current.filterToLen = currB.length;
      prevTypedRef.current.filter = currA;
      prevTypedRef.current.filterTo = currB;
      return;
    }

    if (enteredCompleting) {
      // update typed trackers first so applyModel's internal checks match displayed values
      prevTypedRef.current.filter = currA;
      prevTypedRef.current.filterTo = currB;
      prevInputLenRef.current.filterLen = currA.length;
      prevInputLenRef.current.filterToLen = currB.length;

      if (
        isValidYear(currA) &&
        isValidYear(currB) &&
        Number(currA) <= Number(currB)
      ) {
        // Attempt to apply. force apply to ensure retyping after erase triggers API
        applyModel(
          {
            filterType: "number",
            type: FilterType.IN_RANGE,
            filter: Number(currA),
            filterTo: Number(currB),
          },
          true,
        );
      }
      return;
    }

    if (erasedBothToEmpty) {
      // update trackers first
      prevTypedRef.current.filter = currA;
      prevTypedRef.current.filterTo = currB;
      prevInputLenRef.current.filterLen = currA.length;
      prevInputLenRef.current.filterToLen = currB.length;
      const prevAppliedA = prevFilterValueRef.current || "";
      const prevAppliedB = prevFilterToValueRef.current || "";
      if (prevAppliedA.length !== 0 || prevAppliedB.length !== 0) {
        applyModel(null, true);
      }
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, filterTo, isRange]);
  // computed flag: range order invalid (from > to) when both fields are full
  const sanitizedFrom = sanitizeYearInput(filter);
  const sanitizedTo = sanitizeYearInput(filterTo);
  const isRangeOrderInvalid =
    isRange &&
    sanitizedFrom.length === 4 &&
    sanitizedTo.length === 4 &&
    Number(sanitizedFrom) > Number(sanitizedTo);

  // If user corrects an invalid range (To >= From) after previously having an invalid pair,
  // apply the model immediately (force) so the API is triggered.
  useEffect(() => {
    if (!isRange) return;
    if (sanitizedFrom.length !== 4 || sanitizedTo.length !== 4) return;
    if (isRangeOrderInvalid) return; // still invalid

    // Both are full and valid now. If previously applied model differs or user edited, apply.
    const prevAppliedA = prevFilterValueRef.current || "";
    const prevAppliedB = prevFilterToValueRef.current || "";
    if (
      prevAppliedA === sanitizedFrom &&
      prevAppliedB === sanitizedTo &&
      !dirtySinceApplyRef.current
    ) {
      return; // nothing changed compared to applied model
    }

    // apply the corrected range
    applyModel(
      {
        filterType: "number",
        type: FilterType.IN_RANGE,
        filter: Number(sanitizedFrom),
        filterTo: Number(sanitizedTo),
      },
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedFrom, sanitizedTo, isRangeOrderInvalid, isRange]);
  return (
    <div className="year-filter">
      <select
        value={type}
        onChange={handleTypeChange}
        className="year-filter__select"
        aria-label="Year filter type"
      >
        {FILTER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {!isRange ? (
        <input
          type="text"
          value={filter}
          inputMode="numeric"
          onChange={handleFilterChange}
          maxLength={4}
          placeholder="YYYY"
          className="year-filter__input"
          aria-label="Year"
          aria-invalid={!isSingleValid}
          title={!isSingleValid ? "Enter a valid 4-digit year" : undefined}
        />
      ) : (
        <div className="year-filter__range">
          <input
            type="text"
            value={filter}
            inputMode="numeric"
            onChange={handleFilterChange}
            maxLength={4}
            placeholder="From YYYY"
            className="year-filter__input"
            aria-label="From year"
            aria-invalid={!isRangeValid}
          />
          <input
            type="text"
            value={filterTo}
            inputMode="numeric"
            onChange={handleFilterToChange}
            maxLength={4}
            placeholder={"To YYYY"}
            className={
              "year-filter__input" +
              (isRangeOrderInvalid ? " year-filter__input--invalid" : "")
            }
            aria-label={"To year"}
            aria-invalid={!isRangeValid}
          />
          {isRangeOrderInvalid && (
            <div className="year-filter__warning" role="alert">
              {"'To' must be >= 'From'"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YearFilter;
